// App.js - Main application component with routing and authentication
import React, { useState, useEffect } from 'react';
import './App.css';
import { UserProvider } from './context/UserContext';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import DashboardBanner from './components/Dashboard/DashboardBanner/DashboardBanner';
import Account from './components/Dashboard/Account/Account';
import MiningProducts from './components/Dashboard/MiningProducts/MiningProducts';
import DepositModal from './components/DepositModal/DepositModal';
import Deposit from './pages/Deposit/Deposit';
import RentAsics from './pages/RentAsics/RentAsics';
import RentAsicDetails from './pages/RentAsicDetails/RentAsicDetails';
import Promo from './pages/Promo/Promo';
import BuyAsics from './pages/BuyAsics/BuyAsics';
import ContractReview from './pages/Order/ContractReview';
import BuyAsicsPayment from './pages/Order/BuyAsicsPayment';
import Payment from './pages/Order/Payment';
import TariffDetails from './pages/Order/TariffDetails';
import BuyAsicsOrder from './pages/Order/BuyAsics';
import Marketplace from './pages/Marketplace/Marketplace';
import GeneralSettings from './pages/Settings/GeneralSettings';
import Transactions from './pages/Settings/Transactions';
import Withdraw from './pages/Withdraw/Withdraw';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ChatWidget from './components/ChatWidget/ChatWidget';
import RentedAsics from './pages/RentedAsics/RentedAsics';
import PurchasedAsics from './pages/PurchasedAsics/PurchasedAsics';
import AdminPanel from './pages/Admin/AdminPanel';

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('login'); // Default to login page
  const [currency, setCurrency] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [openChatWidget, setOpenChatWidget] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [selectedAsicId, setSelectedAsicId] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
 
  const [depositCurrency, setDepositCurrency] = useState('BTC');

 
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setIsAuthenticated(true);
        setUser(parsedUser);
      } catch (e) {
        // Invalid user data, clear it
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }
    setAuthLoading(false);
  }, []);

  // Check URL on mount and when it changes
  useEffect(() => {
    if (authLoading) return; // Wait for auth check
    
    const urlParams = new URLSearchParams(window.location.search);
    const urlCurrency = urlParams.get('currency');
    const path = window.location.pathname;
    
    // Public routes (don't require authentication)
    if (path === '/login' || path === '/signin') {
      setCurrentPage('login');
      return;
    }
    
    if (path === '/registration' || path === '/register' || path === '/signup') {
      setCurrentPage('register');
      return;
    }
    
    // Protected routes - redirect to login if not authenticated
    const protectedRoutes = [
      '/deposit', '/mining-store/rent-asics', '/rent-asics', '/mining-store/promo', '/promo',
      '/mining-store/buy-asics', '/buy-asics',
      '/marketplace', '/mining-store/marketplace',
      '/general-settings/settings', '/settings', '/general-settings/transactions', '/transactions',
      '/general-settings/withdrawals', '/withdraw', '/withdrawals', '/order'
    ];
    
    const isProtectedRoute = protectedRoutes.some(route => path === route || path.startsWith(route));
    
    if (isProtectedRoute && !isAuthenticated) {
      setCurrentPage('login');
      window.history.pushState({ page: 'login' }, '', '/login');
      return;
    }
    
    if (path === '/deposit' || urlCurrency) {
      setCurrentPage('deposit');
      if (urlCurrency) {
        setCurrency(urlCurrency);
      }
    } else if (path === '/mining-store/rent-asics' || path === '/rent-asics') {
      setCurrentPage('rent-asics');
    } else if (path.startsWith('/preorder/rent-asic/')) {
      const asicId = path.split('/').pop();
      setSelectedAsicId(asicId);
      setCurrentPage('rent-asic-details');
    } else if (path === '/mining-store/promo' || path === '/promo') {
      setCurrentPage('promo');
    } else if (path === '/mining-store/buy-asics' || path === '/buy-asics') {
      setCurrentPage('buy-asics');
    } else if (path === '/marketplace' || path === '/mining-store/marketplace') {
      setCurrentPage('marketplace');
      } else if (path === '/registration' || path === '/register' || path === '/signup') {
        setCurrentPage('register');
      } else if (path === '/general-settings/settings' || path === '/settings') {
      setCurrentPage('general-settings');
    } else if (path === '/general-settings/transactions' || path === '/transactions') {
      setCurrentPage('transactions');
    } else if (path === '/general-settings/withdrawals' || path === '/withdraw' || path === '/withdrawals') {
      setCurrentPage('withdraw');
    } else if (path === '/dashboard/rented-asics' || path === '/rented-asics') {
      setCurrentPage('rented-asics');
    } else if (path === '/dashboard/purchased-asics' || path === '/purchased-asics') {
      setCurrentPage('purchased-asics');
    } else if (path === '/admin') {
      setCurrentPage('admin');
    } else if (path.startsWith('/order/contract-review/')) {
      setCurrentPage('order');
    } else if (path.startsWith('/order/tariff/')) {
      setCurrentPage('tariff-details');
    } else if (path.startsWith('/order/payment/')) {
      setCurrentPage('payment');
    } else if (path.startsWith('/order/buy-asics/')) {
      setCurrentPage('buy-asics-order');
    } else if (path === '/' || path === '') {
      // Root path - redirect based on authentication status
      if (isAuthenticated) {
        setCurrentPage('dashboard');
      } else {
        setCurrentPage('login');
        window.history.pushState({ page: 'login' }, '', '/login');
      }
      setCurrency(null);
    } else {
      // Default fallback - redirect to login if not authenticated, dashboard if authenticated
      if (isAuthenticated) {
        setCurrentPage('dashboard');
      } else {
        setCurrentPage('login');
        window.history.pushState({ page: 'login' }, '', '/login');
      }
      setCurrency(null);
    }
  }, [authLoading, isAuthenticated]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      if (authLoading) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      const urlCurrency = urlParams.get('currency');
      const path = window.location.pathname;
      
      if (path === '/login' || path === '/signin') {
        setCurrentPage('login');
        return;
      }
      
      if (path === '/registration' || path === '/register' || path === '/signup') {
        setCurrentPage('register');
        return;
      }
      
      if (path.startsWith('/order/buy-asics/')) {
        setCurrentPage('buy-asics-order');
        return;
      }
      
      if (path === '/deposit' || urlCurrency) {
        setCurrentPage('deposit');
        if (urlCurrency) {
          setCurrency(urlCurrency);
        }
      } else if (path === '/mining-store/rent-asics' || path === '/rent-asics') {
        setCurrentPage('rent-asics');
      } else if (path.startsWith('/preorder/rent-asic/')) {
        const asicId = path.split('/').pop();
        setSelectedAsicId(asicId);
        setCurrentPage('rent-asic-details');
      } else if (path === '/mining-store/promo' || path === '/promo') {
        setCurrentPage('promo');
      } else if (path === '/mining-store/buy-asics' || path === '/buy-asics') {
        setCurrentPage('buy-asics');
      } else if (path === '/marketplace' || path === '/mining-store/marketplace') {
        setCurrentPage('marketplace');
      } else if (path === '/registration' || path === '/register' || path === '/signup') {
        setCurrentPage('register');
      } else if (path === '/general-settings/settings' || path === '/settings') {
        setCurrentPage('general-settings');
      } else if (path === '/general-settings/transactions' || path === '/transactions') {
        setCurrentPage('transactions');
      } else if (path === '/general-settings/withdrawals' || path === '/withdraw' || path === '/withdrawals') {
        setCurrentPage('withdraw');
      } else if (path === '/dashboard/rented-asics' || path === '/rented-asics') {
        setCurrentPage('rented-asics');
      } else if (path === '/dashboard/purchased-asics' || path === '/purchased-asics') {
        setCurrentPage('purchased-asics');
      } else if (path === '/admin') {
        setCurrentPage('admin');
      } else if (path.startsWith('/order/contract-review/')) {
        setCurrentPage('order');
      } else if (path.startsWith('/order/tariff/')) {
        setCurrentPage('tariff-details');
      } else if (path.startsWith('/order/payment/')) {
        setCurrentPage('payment');
      } else if (path.startsWith('/order/buy-asics/')) {
        setCurrentPage('buy-asics-payment');
      } else {
        setCurrentPage('dashboard');
        setCurrency(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [authLoading]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const navigateToDeposit = (selectedCurrency) => {
    setCurrency(selectedCurrency);
    setCurrentPage('deposit');
    // Update URL without page reload
    if (selectedCurrency) {
      window.history.pushState({ page: 'deposit', currency: selectedCurrency }, '', `/deposit?currency=${selectedCurrency}`);
    } else {
      window.history.pushState({ page: 'deposit' }, '', '/deposit');
    }
  };

  const handleNavigation = (path) => {
    if (path === '/mining-store/rent-asics' || path === '/rent-asics') {
      navigateToRentAsics();
    } else if (path === '/mining-store/promo' || path === '/promo') {
      navigateToPromo();
    } else if (path === '/mining-store/buy-asics' || path === '/buy-asics') {
      navigateToBuyAsics();
    } else if (path === '/marketplace' || path === '/mining-store/marketplace') {
      navigateToMarketplace();
    } else if (path === '/general-settings/settings' || path === '/settings') {
      navigateToGeneralSettings();
    } else if (path === '/general-settings/transactions' || path === '/transactions') {
      navigateToTransactions();
    } else if (path === '/general-settings/withdrawals' || path === '/withdraw' || path === '/withdrawals') {
      navigateToWithdraw();
    } else if (path === '/deposit') {
      navigateToDeposit(null);
    } else if (path === '/') {
      // Root path - redirect based on authentication status
      if (isAuthenticated) {
        navigateToDashboard();
      } else {
        navigateToLogin();
      }
    }
    // Close sidebar on mobile after navigation
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const navigateToDashboard = () => {
    setCurrentPage('dashboard');
    setCurrency(null);
    window.history.pushState({ page: 'dashboard' }, '', '/');
  };

  const navigateToRentAsics = () => {
    setCurrentPage('rent-asics');
    window.history.pushState({ page: 'rent-asics' }, '', '/mining-store/rent-asics');
  };

  const navigateToRentAsicDetails = (asicId) => {
    setSelectedAsicId(asicId);
    setCurrentPage('rent-asic-details');
    window.history.pushState({ page: 'rent-asic-details', asicId }, '', `/preorder/rent-asic/${asicId}`);
  };

  const navigateBackFromAsicDetails = () => {
    setCurrentPage('rent-asics');
    setSelectedAsicId(null);
    window.history.pushState({ page: 'rent-asics' }, '', '/mining-store/rent-asics');
  };

  const navigateToOrder = (asicId, quantity) => {
    // This would navigate to order page - implement as needed
    console.log('Navigate to order with ASIC ID:', asicId, 'Quantity:', quantity);
  };

  const navigateToPromo = () => {
    setCurrentPage('promo');
    window.history.pushState({ page: 'promo' }, '', '/mining-store/promo');
  };

  const navigateToBuyAsics = () => {
    setCurrentPage('buy-asics');
    window.history.pushState({ page: 'buy-asics' }, '', '/mining-store/buy-asics');
  };

  const navigateToMarketplace = () => {
    setCurrentPage('marketplace');
    window.history.pushState({ page: 'marketplace' }, '', '/marketplace');
  };

  const navigateToGeneralSettings = () => {
    setCurrentPage('general-settings');
    window.history.pushState({ page: 'general-settings' }, '', '/general-settings/settings');
  };

  const navigateToTransactions = () => {
    setCurrentPage('transactions');
    window.history.pushState({ page: 'transactions' }, '', '/general-settings/transactions');
  };

  const navigateToWithdraw = () => {
    setCurrentPage('withdraw');
    window.history.pushState({ page: 'withdraw' }, '', '/general-settings/withdrawals');
  };

  const navigateToLogin = () => {
    setCurrentPage('login');
    window.history.pushState({ page: 'login' }, '', '/login');
  };

  const navigateToRegister = () => {
    setCurrentPage('register');
    window.history.pushState({ page: 'register' }, '', '/registration');
  };

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    setCurrentPage('dashboard');
    window.history.pushState({ page: 'dashboard' }, '', '/');
  };

  const handleRegister = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    
    // Add user to admin customers list
    const savedCustomers = localStorage.getItem('adminCustomers');
    let customers = [];
    if (savedCustomers) {
      try {
        customers = JSON.parse(savedCustomers);
      } catch (e) {
        console.error('Error parsing customers:', e);
      }
    }
    
    // Check if user already exists
    const userExists = customers.find(c => c.email === userData.email);
    if (!userExists) {
      const newCustomer = {
        id: Date.now(),
        email: userData.email,
        name: userData.name || userData.email.split('@')[0],
        registrationDate: new Date().toISOString(),
        status: 'active',
        emailVerified: true,
        phoneVerified: false,
        totalDeposits: 0,
        totalWithdrawals: 0,
        balance: 0,
      };
      customers.unshift(newCustomer);
      localStorage.setItem('adminCustomers', JSON.stringify(customers));
    }
    
    setCurrentPage('dashboard');
    window.history.pushState({ page: 'dashboard' }, '', '/');
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setIsAuthenticated(false);
    setUser(null);
    navigateToLogin();
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Show admin panel (standalone, no header/sidebar)
  if (currentPage === 'admin') {
    return (
      <UserProvider>
        <AdminPanel />
      </UserProvider>
    );
  }

  // Show login/register pages (public routes)
  if (currentPage === 'login') {
    return (
      <div className="App">
        <Login 
          onLogin={handleLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToForgot={() => {
            // Handle forgot password - can be implemented later
            console.log('Forgot password');
          }}
        />
      </div>
    );
  }

  if (currentPage === 'register') {
    return (
      <div className="App">
        <Register 
          onRegister={handleRegister}
          onNavigateToLogin={navigateToLogin}
          onNavigateToDashboard={() => {
            setCurrentPage('dashboard');
            window.history.pushState({ page: 'dashboard' }, '', '/');
          }}
        />
      </div>
    );
  }

  return (
    <UserProvider>
    <div className="App">
      <Header 
        onMenuClick={toggleSidebar} 
        onNavigateToDeposit={navigateToDeposit}
        onNavigateToWithdraw={navigateToWithdraw}
        isAuthenticated={isAuthenticated}
        user={user}
        onLogout={handleLogout}
        onNavigateToLogin={navigateToLogin}
        onOpenChat={() => setOpenChatWidget(prev => !prev)}
      />
      <div className="layout">
        <div 
          className={`layout__overlay ${sidebarOpen ? 'active' : ''}`} 
          onClick={closeSidebar}
        ></div>
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} onNavigate={handleNavigation} />
        <div className="layout__wrapper container">
          {currentPage === 'dashboard' ? (
            <main className="main">
              <DashboardBanner />
              <Account onNavigateToDeposit={navigateToDeposit} />
              <MiningProducts />
            </main>
          ) : currentPage === 'rent-asics' ? (
            <RentAsics onNavigateToDetails={navigateToRentAsicDetails} />
          ) : currentPage === 'rent-asic-details' ? (
            <RentAsicDetails 
              asicId={selectedAsicId} 
              onNavigateBack={navigateBackFromAsicDetails}
              onNavigateToOrder={navigateToOrder}
            />
          ) : currentPage === 'promo' ? (
            <Promo />
          ) : currentPage === 'buy-asics' ? (
            <BuyAsics />
          ) : currentPage === 'marketplace' ? (
            <Marketplace />
          ) : currentPage === 'general-settings' ? (
            <GeneralSettings />
          ) : currentPage === 'transactions' ? (
            <Transactions />
          ) : currentPage === 'withdraw' ? (
            <Withdraw />
          ) : currentPage === 'rented-asics' ? (
            <RentedAsics />
          ) : currentPage === 'purchased-asics' ? (
            <PurchasedAsics />
          ) : currentPage === 'order' ? (
            <ContractReview onBack={navigateToRentAsics} />
          ) : currentPage === 'tariff-details' ? (
            <TariffDetails />
          ) : currentPage === 'payment' ? (
            <Payment onBack={() => {
              const pathParts = window.location.pathname.split('/');
              const url = `/order/contract-review/${pathParts[3]}/${pathParts[4]}/${pathParts[5]}/${pathParts[6]}`;
              window.history.pushState({ page: 'order' }, '', url);
              window.location.reload();
            }} />
          ) : currentPage === 'buy-asics-payment' ? (
            <BuyAsicsPayment />
          ) : currentPage === 'buy-asics-order' ? (
            <BuyAsicsOrder />
          ) : (
            <Deposit currency={currency} onBack={navigateToDashboard} />
          )}
        </div>
      </div>
      {isAuthenticated && currentPage !== 'admin' && (
        <ChatWidget 
          isAuthenticated={isAuthenticated} 
          externalOpen={openChatWidget}
          onClose={() => setOpenChatWidget(false)}
        />
      )}
      
      {/* Global Deposit Modal */}
      <DepositModal 
        isOpen={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        currency={depositCurrency}
      />
    </div>
    </UserProvider>
  );
}

export default App;

